import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, Minus, Plus, Trash2, ShoppingBag } from 'lucide-react';
import { useCart } from '../context/CartContext';

const EmptyState = ({ onClose }) => (
  <div className="flex-1 flex flex-col items-center justify-center gap-3 px-8 text-center">
    <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center">
      <ShoppingBag size={28} className="text-red-600" />
    </div>
    <p className="text-[#0a0a0a] font-semibold">Your cart is empty</p>
    <p className="text-sm text-slate-500">Add products to get started.</p>
    <Link to="/shop" onClick={onClose} className="mt-2 px-5 py-2.5 bg-red-500 hover:bg-red-400 text-white text-sm font-semibold rounded-lg transition-colors">
      Browse Products
    </Link>
  </div>
);

const BundleBanner = ({ uniqueItems }) => (
  <div className="px-6 py-3 bg-red-50/70 border-b border-red-100 text-sm text-red-800">
    {uniqueItems >= 3
      ? <span>🎉 Bundle discount applied — 15% off!</span>
      : <span>Add <b>{3 - uniqueItems}</b> more unique item{3 - uniqueItems === 1 ? '' : 's'} to unlock 15% off.</span>}
  </div>
);

const QtyStepper = ({ qty, onInc, onDec }) => (
  <div className="inline-flex items-center border border-slate-200 rounded-lg">
    <button onClick={onDec} className="p-1.5 text-slate-600 hover:text-red-600" aria-label="Decrease"><Minus size={13} /></button>
    <span className="w-7 text-center text-sm font-medium">{qty}</span>
    <button onClick={onInc} className="p-1.5 text-slate-600 hover:text-red-600" aria-label="Increase"><Plus size={13} /></button>
  </div>
);

const LineItem = ({ item, onRemove, onInc, onDec }) => (
  <div className="flex gap-4 pb-4 border-b border-slate-100 last:border-0">
    <div className="w-20 h-20 rounded-lg bg-slate-50 flex-shrink-0 overflow-hidden">
      {item.image
        ? <img src={item.image} alt={item.name} className="w-full h-full object-contain p-2" />
        : <div className="w-full h-full flex items-center justify-center text-slate-300 text-[10px]">No image</div>}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex justify-between gap-3">
        <h4 className="font-semibold text-sm text-[#0a0a0a] line-clamp-2">{item.name}</h4>
        <button onClick={onRemove} className="text-slate-400 hover:text-red-500 transition-colors" aria-label="Remove">
          <Trash2 size={16} />
        </button>
      </div>
      <div className="text-xs text-red-700 font-medium mt-0.5">{item.purity}</div>
      <div className="flex items-center justify-between mt-3">
        <QtyStepper qty={item.qty} onInc={onInc} onDec={onDec} />
        <div className="font-semibold text-[#0a0a0a] text-sm">${(item.price * item.qty).toFixed(2)}</div>
      </div>
    </div>
  </div>
);

const Totals = ({ subtotal, bundleDiscount, total, onClose }) => (
  <div className="border-t border-slate-200 px-6 py-5 space-y-3 bg-slate-50/50">
    <div className="flex justify-between text-sm text-slate-600"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
    {bundleDiscount > 0 && (
      <div className="flex justify-between text-sm text-red-700"><span>Bundle discount (15%)</span><span>− ${bundleDiscount.toFixed(2)}</span></div>
    )}
    <div className="flex justify-between text-base font-semibold text-[#0a0a0a] pt-2 border-t border-slate-200"><span>Total</span><span>${total.toFixed(2)} AUD</span></div>
    <Link to="/checkout" onClick={onClose} data-testid="cart-checkout-btn" className="block w-full mt-2 py-3.5 bg-red-500 hover:bg-red-400 text-white font-semibold rounded-lg transition-colors text-center">
      Checkout
    </Link>
    <p className="text-[11px] text-slate-500 text-center">Pay by PayID — no card fees</p>
  </div>
);

const CartDrawer = () => {
  const { items, isOpen, setIsOpen, removeItem, updateQty, subtotal, bundleDiscount, total, uniqueItems } = useCart();
  const close = () => setIsOpen(false);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  return (
    <>
      <div
        onClick={close}
        className={`fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 transition-opacity duration-300 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      />
      <aside className={`fixed top-0 right-0 h-full w-full sm:w-[420px] bg-white z-50 shadow-2xl flex flex-col transition-transform duration-300 ease-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-[#0a0a0a] flex items-center gap-2">
            <ShoppingBag size={18} className="text-red-600" /> Your Cart
          </h2>
          <button onClick={close} className="p-2 rounded-full hover:bg-slate-100 text-slate-600 transition-colors" aria-label="Close cart">
            <X size={20} />
          </button>
        </div>

        {items.length === 0 ? <EmptyState onClose={close} /> : (
          <>
            <BundleBanner uniqueItems={uniqueItems} />
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {items.map((item) => (
                <LineItem
                  key={item.id}
                  item={item}
                  onRemove={() => removeItem(item.id)}
                  onInc={() => updateQty(item.id, item.qty + 1)}
                  onDec={() => updateQty(item.id, item.qty - 1)}
                />
              ))}
            </div>
            <Totals subtotal={subtotal} bundleDiscount={bundleDiscount} total={total} onClose={close} />
          </>
        )}
      </aside>
    </>
  );
};

export default CartDrawer;
